import { useMemo } from "react";
const PRECISION = 10n ** 18n;
export default function useSankeyGraph(sdk, graph, result, palette) {
    return useMemo(() => {
        const path = result.optimalPath ?? [];
        if (path.length === 0) {
            return { nodes: [], links: [] };
        }
        const edgeIds = path.map(v => v.id);
        const nodes = Array.from(new Set(graph.edges
            .filter(v => edgeIds.includes(v.id))
            .flatMap(e => [e.tokenIn, e.tokenOut])));
        const edges = path.map(s => ({
            ...s,
            ...graph.edges.find(e => e.id === s.id),
        }));
        const edgesById = new Map(edges.map(e => [e.id, e]));
        const inflows = new Map();
        const rootTokenInflow = new Map([
            [result.tokenIn, BigInt(result.amountIn)],
        ]);
        // tokens, for which we can trace all paths to root token
        for (const step of path) {
            const edge = edgesById.get(step.id);
            const inflow = inflows.get(edge.tokenIn) ?? 0n;
            inflows.set(edge.tokenIn, inflow + BigInt(step.amountIn));
        }
        const edgeFlows = new Map();
        const rootEdgeFlows = new Map();
        const queue = [...edges];
        while (queue.length > 0) {
            const edge = queue.shift();
            const isDangling = queue.every(other => other.tokenOut !== edge.tokenIn);
            if (isDangling) {
                // find share of all traffic that goes into this edge from it's inflow
                const allInflow = inflows.get(edge.tokenIn);
                const share = (PRECISION * BigInt(edge.amountIn)) / allInflow;
                // share of all root token inflow that goes through this edge
                const rootInflow = (share * rootTokenInflow.get(edge.tokenIn)) / PRECISION;
                const percentage = Number((rootInflow * 1000000n) / BigInt(result.amountIn));
                rootEdgeFlows.set(edge.id, Math.round(percentage / 10000));
                edgeFlows.set(edge.id, Math.round(Number((1000000n * share) / PRECISION) / 100) / 100);
                const destRootInflow = rootTokenInflow.get(edge.tokenOut) ?? 0n;
                // add rootTokenInflow on the receiving side
                rootTokenInflow.set(edge.tokenOut, destRootInflow + rootInflow);
            }
            else {
                queue.push(edge);
            }
        }
        const tokenToColor = new Map(nodes.map((token, i) => [token, palette[i % palette.length]]));
        return {
            nodes: nodes.map(token => ({
                token,
                symbol: sdk.tokensMeta.symbol(token) ?? token,
            })),
            links: edges.map((e, i) => ({
                tokenIn: e.tokenIn,
                tokenOut: e.tokenOut,
                adapter: e.adapterType.replace("ADAPTER::", ""),
                amountIn: BigInt(path[i].amountIn),
                amountOut: BigInt(path[i].amountOut),
                source: nodes.indexOf(e.tokenIn),
                target: nodes.indexOf(e.tokenOut),
                value: rootEdgeFlows.get(e.id) || 0,
                parentShare: edgeFlows.get(e.id) || 0,
                color: tokenToColor.get(e.tokenOut),
            })),
        };
    }, [sdk, graph, result, palette]);
}
